/**
 * Dev hub — persistent rolling log of the last 40 API calls (FIFO).
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { isDevHubClient } from '../environment/devHub';
import type { DevApiCallHistoryEntry } from './types';

export const DEV_API_CALL_HISTORY_MAX = 40;
const STORAGE_KEY = '@dev_api_call_history_v1';

let entries: DevApiCallHistoryEntry[] = [];
let hydrated = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach(fn => {
    try {
      fn();
    } catch {
      // ignore subscriber errors
    }
  });
}

function parse_stored_body(raw: unknown): unknown {
  if (typeof raw !== 'string') {
    return raw;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export function subscribeDevApiCallHistory(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDevApiCallHistoryEntries(): DevApiCallHistoryEntry[] {
  return [...entries];
}

export function getDevApiCallHistoryCount(): number {
  return entries.length;
}

export async function loadDevApiCallHistoryFromStorage(): Promise<DevApiCallHistoryEntry[]> {
  if (!isDevHubClient()) {
    entries = [];
    hydrated = true;
    return [];
  }
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) {
      entries = [];
    } else {
      const parsed = JSON.parse(raw) as unknown;
      entries = Array.isArray(parsed)
        ? (parsed as DevApiCallHistoryEntry[]).slice(-DEV_API_CALL_HISTORY_MAX)
        : [];
    }
  } catch {
    entries = [];
  }
  hydrated = true;
  notify();
  return getDevApiCallHistoryEntries();
}

async function persist_entries(): Promise<void> {
  if (!isDevHubClient()) {
    return;
  }
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // best-effort
  }
}

function create_entry_id(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function captureDevApiCallHistory(input: {
  method: string;
  path: string;
  params?: unknown;
  body?: unknown;
  response?: unknown;
  error?: string;
  status?: number;
}): void {
  if (!isDevHubClient()) {
    return;
  }

  const entry: DevApiCallHistoryEntry = {
    id: create_entry_id(),
    at: new Date().toISOString(),
    method: String(input.method ?? 'GET').toUpperCase(),
    path: String(input.path ?? '').replace(/^\//, ''),
    request_params: input.params,
    request_body: parse_stored_body(input.body),
    http_status: input.status,
    response: input.response,
    error: input.error,
  };

  entries.push(entry);
  if (entries.length > DEV_API_CALL_HISTORY_MAX) {
    entries = entries.slice(entries.length - DEV_API_CALL_HISTORY_MAX);
  }

  notify();
  void persist_entries();
}

export async function clearDevApiCallHistory(): Promise<void> {
  entries = [];
  hydrated = true;
  notify();
  if (!isDevHubClient()) {
    return;
  }
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function exportDevApiCallHistoryJson(selected_ids?: Set<string>): string {
  const list =
    selected_ids != null && selected_ids.size > 0
      ? entries.filter(e => selected_ids.has(e.id))
      : [...entries];
  return JSON.stringify(
    {
      exported_at: new Date().toISOString(),
      count: list.length,
      max_capacity: DEV_API_CALL_HISTORY_MAX,
      entries: list,
    },
    null,
    2,
  );
}

export function exportDevApiCallHistoryText(selected_ids?: Set<string>): string {
  const list =
    selected_ids != null && selected_ids.size > 0
      ? entries.filter(e => selected_ids.has(e.id))
      : [...entries];
  const lines: string[] = [
    'Dev API call history',
    `Exported: ${new Date().toISOString()}`,
    `Entries: ${list.length} (max ${DEV_API_CALL_HISTORY_MAX})`,
    '',
  ];
  for (const entry of list) {
    lines.push(`--- ${entry.at} ---`);
    lines.push(`${entry.method} ${entry.path}${entry.http_status != null ? ` → ${entry.http_status}` : ''}`);
    if (entry.error) {
      lines.push(`error: ${entry.error}`);
    }
    if (entry.request_params != null) {
      lines.push(`params: ${JSON.stringify(entry.request_params)}`);
    }
    if (entry.request_body != null) {
      lines.push(`body: ${JSON.stringify(entry.request_body)}`);
    }
    if (entry.response !== undefined) {
      lines.push(`response: ${JSON.stringify(entry.response)}`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

/** Ensures storage is loaded before first read in UI (no-op if already hydrated). */
export async function ensureDevApiCallHistoryHydrated(): Promise<void> {
  if (hydrated) {
    return;
  }
  await loadDevApiCallHistoryFromStorage();
}
